import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { apiErrorMessage, apiFetch } from '@/lib/api'
import {
  SESSION_QUERY_KEY,
  landingFor,
  useSession,
  type SessionUser,
} from '@/lib/auth'
import Header from '@/components/layout/Header'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Wpisz obecne hasło'),
    newPassword: z
      .string()
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(200, 'Hasło jest za długie'),
    confirmNewPassword: z.string().min(1, 'Potwierdź nowe hasło'),
  })
  .refine((v) => v.newPassword === v.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Hasła nie są identyczne',
  })

type FormValues = z.infer<typeof schema>

export default function ChangePassword() {
  const session = useSession()
  const user = session.data
  const forced = user?.mustChangePassword === true
  const navigate = useNavigate()
  const qc = useQueryClient()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  })

  const change = useMutation({
    mutationFn: (values: { currentPassword: string; newPassword: string }) =>
      apiFetch<{ ok: true }>('/api/auth/change-password', {
        method: 'POST',
        body: values,
      }),
    onSuccess: () => {
      const updated: SessionUser | null = user
        ? { ...user, mustChangePassword: false }
        : null
      qc.setQueryData(SESSION_QUERY_KEY, updated)
      toast.success('Hasło zmienione')
      navigate(updated ? landingFor(updated) : '/login', { replace: true })
    },
  })

  const submitError = change.error ? apiErrorMessage(change.error) : null

  return (
    <div className="min-h-[100svh] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
          <h1 className="text-2xl font-semibold">Zmiana hasła</h1>
          {forced && (
            <p className="mt-2 text-sm text-text-muted">
              Twoje konto wymaga ustawienia nowego hasła przed dalszym
              korzystaniem z aplikacji.
            </p>
          )}

          <form
            onSubmit={form.handleSubmit(({ currentPassword, newPassword }) =>
              change.mutate({ currentPassword, newPassword }),
            )}
            noValidate
            className="mt-6 space-y-4"
          >
            <div>
              <Label htmlFor="currentPassword">Obecne hasło</Label>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                autoFocus
                invalid={!!form.formState.errors.currentPassword}
                {...form.register('currentPassword')}
              />
              {form.formState.errors.currentPassword && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.currentPassword.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="newPassword">Nowe hasło</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                invalid={!!form.formState.errors.newPassword}
                {...form.register('newPassword')}
              />
              {form.formState.errors.newPassword && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.newPassword.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="confirmNewPassword">Powtórz nowe hasło</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                autoComplete="new-password"
                invalid={!!form.formState.errors.confirmNewPassword}
                {...form.register('confirmNewPassword')}
              />
              {form.formState.errors.confirmNewPassword && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.confirmNewPassword.message}
                </p>
              )}
            </div>

            {submitError && <Alert variant="error">{submitError}</Alert>}

            <Button type="submit" loading={change.isPending} className="w-full">
              Zmień hasło
            </Button>
          </form>
        </div>
      </main>
    </div>
  )
}
